/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { EntityType, UserRole } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface AccessibleItemProps {
  id: string;
  type: EntityType;
  entityId: string;
  userRoles: Set<UserRole> | null;
  personId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class AccessibleItemRecord extends Record<AccessibleItemProps>({
  id: null as any,
  type: null as any,
  entityId: null as any,
  userRoles: null as any,
  personId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    type: EntityType;
    entityId: string;
    userRoles: any;
    personId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): AccessibleItemRecord {
    if (json == null) {
      throw new Error('AccessibleItemRecord.fromJS: parameter cannot be null');
    }
    return new AccessibleItemRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'AccessibleItemJson.id',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', EntityType.Unspecified),
        'AccessibleItemJson.type',
      ),
      entityId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
        'AccessibleItemJson.entityId',
      ),
      userRoles: json.userRoles != null ? Set<UserRole>(json.userRoles) : null,
      personId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
        'AccessibleItemJson.personId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    type: EntityType;
    entityId: string;
    userRoles: any;
    personId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [AccessibleItemRecord, Error[]] {
    if (json == null) {
      throw new Error('AccessibleItemRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new AccessibleItemRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'AccessibleItemJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', EntityType.Unspecified),
            'AccessibleItemJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'entityId', '00000000-0000-0000-0000-000000000000'),
            'AccessibleItemJson.entityId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      userRoles: tryOrDefault(
        () => (json.userRoles != null ? Set<UserRole>(json.userRoles) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      personId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
            'AccessibleItemJson.personId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: AccessibleItemProps) {
    super(props);
    if (props == null) {
      throw new Error('AccessibleItemRecord constructor parameter cannot be null');
    }
  }
}

export abstract class AccessibleItemViewModelBase implements AccessibleItemProps {
  protected readonly _props: AccessibleItemProps;
  private _json?: AccessibleItemProps;

  get id(): string {
    return this._props.id;
  }
  get type(): EntityType {
    return this._props.type;
  }
  get entityId(): string {
    return this._props.entityId;
  }
  get userRoles(): Set<UserRole> | null {
    return this._props.userRoles;
  }
  get personId(): string {
    return this._props.personId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAirport() {
    return this._props.type === EntityType.Airport;
  }
  public get isBase() {
    return this._props.type === EntityType.Base;
  }
  public get isCard() {
    return this._props.type === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.type === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.type === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.type === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.type === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.type === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.type === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.type === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.type === EntityType.Person;
  }
  public get isProgram() {
    return this._props.type === EntityType.Program;
  }
  public get isRegion() {
    return this._props.type === EntityType.Region;
  }
  public get isTimer() {
    return this._props.type === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.type === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.type === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.type === EntityType.Vehicle;
  }

  public isAnyType(...entityType: EntityType[]) {
    return entityType.includes(this._props.type);
  }

  constructor(props: AccessibleItemProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        type: this._props.type,
        entityId: this._props.entityId,
        userRoles: this._props.userRoles,
        personId: this._props.personId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllAccessibleItemEntities = (state: AppState) =>
  state.entityStores.accessibleItem.entities;

export const selectAccessibleItemById = (id: string) => (state: AppState) =>
  state.entityStores.accessibleItem.entities.get(id);

export const selectAccessibleItemMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.accessibleItem.meta.get(id);

export const selectAccessibleItemAndMetadataById = (id: string) =>
  createSelector(
    selectAccessibleItemById(id),
    selectAccessibleItemMetadataById(id),
    (accessibleItem, meta) => ({ accessibleItem, meta }),
  );

export function selectAccessibleItemEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllAccessibleItemEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, AccessibleItemRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectAccessibleItemEntitiesByType(type: EntityType) {
  return createSelector(selectAllAccessibleItemEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectAccessibleItemEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllAccessibleItemEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectAccessibleItemEntitiesByPersonId(personId: string) {
  return createSelector(selectAllAccessibleItemEntities, (entities) =>
    entities.filter((e) => e.personId === personId).valueSeq(),
  );
}
//#endregion Selectors
